import { CFormTextarea } from "@coreui/react";
import { Controller, type Control, useFieldArray } from "react-hook-form";
import { Input } from "../../../../components/inputs/Input";
import { Field, FormSection } from "../../../../components/ui/FormUi";
import { SwitchCard } from "../../../../components/ui/ToggleUi";

interface CreateDiscountFormProps {
  control: Control<any, any>;
  isActive?: boolean;
  isPublic?: boolean;
  isAdvanced: boolean;
  setIsAdvanced: (value: boolean) => void;
  mode?: "create" | "update";
}

const innerCard: React.CSSProperties = {
  border: "1px solid #eee",
  borderRadius: 12,
  backgroundColor: "#fcfcfc",
};

export default function CreateDiscountForm({
  control,
  isAdvanced = false,
  setIsAdvanced,
}: CreateDiscountFormProps) {
  return (
    <>
      {/* General */}
      <FormSection title="Informations générales">
        <div className="col-md-6">
          <Input
            name="name"
            control={control}
            type="text"
            label="Nom"
            placeholder="Nom"
          />
        </div>
        <div className="col-md-6">
          <Input
            name="experationDate"
            control={control}
            type="date"
            label="Date d'expiration"
            placeholder="Date d'expiration"
          />
        </div>
        <div className="col-md-6">
          <Input
            name="maxUsage"
            control={control}
            type="number"
            min={1}
            label="Utilisation maximale"
          />
        </div>
        <div className="col-md-6">
          <Input
            name="maxPerUser"
            control={control}
            type="number"
            min={1}
            label="Utilisation par utilisateur"
          />
        </div>

        <div className="col-md-6">
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <SwitchCard
                label="Actif"
                description="Le coupon peut être utilisé"
                checked={!!field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
        </div>
        <div className="col-md-6">
          <Controller
            name="isPublic"
            control={control}
            render={({ field }) => (
              <SwitchCard
                label="Public"
                description="Visible par tous les utilisateurs"
                checked={!!field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
        </div>
      </FormSection>

      {/* Discount value */}
      <section
        style={{
          border: "1px solid #eee",
          borderRadius: 16,
          padding: 16,
          backgroundColor: "#fff",
        }}
        className="mb-3"
      >
        <div
          className="d-flex justify-content-between align-items-center mb-3 pb-2"
          style={{ borderBottom: "1px solid #f0f0f0" }}
        >
          <div>
            <h6 className="fw-bold mb-0" style={{ fontSize: 15 }}>
              {isAdvanced ? "Remises avancées" : "Valeur de la remise"}
            </h6>
            <div className="text-muted" style={{ fontSize: 12 }}>
              {isAdvanced
                ? "Définissez des remises par inscription et par usage"
                : "Un pourcentage unique pour chaque utilisation"}
            </div>
          </div>

          {/* Mode segmented toggle */}
          <div
            className="d-inline-flex p-1 rounded-pill"
            style={{ backgroundColor: "#f2f2f2" }}
          >
            {[
              { v: false, label: "Simple" },
              { v: true, label: "Avancé" },
            ].map((m) => (
              <button
                key={m.label}
                type="button"
                onClick={() => setIsAdvanced(m.v)}
                className="border-0 rounded-pill px-3"
                style={{
                  height: 28,
                  fontSize: 12,
                  fontWeight: 700,
                  color: isAdvanced === m.v ? "#fff" : "#666",
                  backgroundColor:
                    isAdvanced === m.v ? "#f0813f" : "transparent",
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {isAdvanced ? (
          <AdvancedDiscountSection control={control} />
        ) : (
          <div className="row g-3">
            <Input
              name="discount.0.registraction"
              control={control}
              type="hidden"
              defaultValue={1}
            />
            <Input
              name="discount.0.value.0.usage"
              control={control}
              type="hidden"
              defaultValue={1}
            />
            <div className="col-md-6">
              <Input
                name="discount.0.value.0.value"
                control={control}
                type="number"
                min={0}
                label="Valeur de la remise (%)"
              />
            </div>
          </div>
        )}
      </section>

      {/* Comments */}
      <FormSection title="Commentaires">
        <Controller
          name="comments"
          control={control}
          render={({ field }) => (
            <Field col="col-12" label="Commentaires" htmlFor="comments">
              <CFormTextarea
                id="comments"
                rows={3}
                placeholder="Commentaires"
                {...field}
                value={field.value ?? ""}
              />
            </Field>
          )}
        />
      </FormSection>
    </>
  );
}

const AdvancedDiscountSection = ({ control }: { control: any }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "discount",
  });

  return (
    <div>
      {fields.length === 0 && (
        <div
          className="text-center text-muted py-3 mb-3"
          style={{
            border: "1px dashed #ddd",
            borderRadius: 12,
            fontSize: 13,
          }}
        >
          Aucune remise
        </div>
      )}

      <div className="d-flex flex-column gap-3">
        {fields.map((field, index) => (
          <DiscountItem
            key={field.id}
            control={control}
            index={index}
            onRemove={() => remove(index)}
          />
        ))}
      </div>

      <button
        type="button"
        className="btn btn-sm btn-outline-primary rounded-pill px-3 mt-3"
        onClick={() => append({})}
      >
        + Ajouter une remise
      </button>
    </div>
  );
};

const DiscountItem = ({
  control,
  index,
  onRemove,
}: {
  control: any;
  index: number;
  onRemove: () => void;
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `discount.${index}.value`,
  });

  return (
    <div className="p-3" style={innerCard}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="fw-bold" style={{ fontSize: 14 }}>
          Remise {index + 1}
        </span>
        <button
          type="button"
          className="btn btn-sm btn-outline-danger rounded-pill px-3"
          onClick={onRemove}
        >
          Supprimer la remise
        </button>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <Input
            name={`discount.${index}.registraction`}
            control={control}
            type="number"
            min={0}
            max={100}
            label="Inscription"
          />
        </div>
      </div>

      <div
        className="text-muted mb-2"
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
        }}
      >
        Usages du coupon
      </div>

      {fields.length === 0 && (
        <div className="text-muted mb-2" style={{ fontSize: 13 }}>
          Aucun usage
        </div>
      )}

      <div className="d-flex flex-column gap-2">
        {fields.map((field, idx) => (
          <div
            key={field.id}
            className="d-flex align-items-end gap-3 p-2"
            style={{ ...innerCard, backgroundColor: "#fff" }}
          >
            <div className="flex-grow-1">
              <Input
                name={`discount.${index}.value.${idx}.usage`}
                control={control}
                type="number"
                label="Intervalle d'utilisation"
              />
            </div>
            <div className="flex-grow-1">
              <Input
                name={`discount.${index}.value.${idx}.value`}
                control={control}
                type="number"
                label="Valeur de la remise (%)"
                min={0}
              />
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger rounded-pill px-3 mb-1"
              onClick={() => remove(idx)}
            >
              Supprimer
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="btn btn-sm btn-outline-primary rounded-pill px-3 mt-3"
        onClick={() => append({})}
      >
        + Ajouter un usage
      </button>
    </div>
  );
};
